import { CoverageTiers } from './lrp/LrpInsurance';

export enum CampaignStatus {
  ACTIVE = 'ACTIVE', // currently running campaign
  PAUSED = 'PAUSED', // paused by user (would be running)
  SCHEDULED = 'SCHEDULED', // scheduled to start in future
  EXPIRED = 'EXPIRED', // expired or ended
}

// TODO: verify options
export enum CampaignFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum CampaignRecipientStatus {
  ACTIVE = 'ACTIVE',
  UNSUBSCRIBED = 'UNSUBSCRIBED',
  INVALID = 'INVALID', // contact info invalid
}

export enum FrequencyDayOfWeek {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
}

export const DayOfWeekIndexMap = {
  [FrequencyDayOfWeek.MONDAY]: 1,
  [FrequencyDayOfWeek.TUESDAY]: 2,
  [FrequencyDayOfWeek.WEDNESDAY]: 3,
  [FrequencyDayOfWeek.THURSDAY]: 4,
  [FrequencyDayOfWeek.FRIDAY]: 5,
};

export const DayOfWeekIndexToNameMap = Object.entries(DayOfWeekIndexMap).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {} as Record<number, string>
);

export enum CampaignTimeframeTypes {
  ONGOING = 'ONGOING',
  FIXED = 'FIXED',
}

export const CampaignCoverageTierOptions = [
  { value: CoverageTiers.TOP, label: 'Top Level' },
  { value: CoverageTiers.ALL, label: 'All Levels' },
];

export const CampaignMarketingMonthsAll: number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
];

export const CampaignMarketingLengthsCattle: number[] = [
  2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
];

export const CampaignMarketingLengthsSwine: number[] = [2, 3, 4, 5, 6];

export const LgmCampaignPdfColumns = {
  MARKETING_MONTH: 'Marketing Month',
  DEDUCTIBLE: 'Deductible',
  GROSS_MARGIN_GUARANTEE: 'Gross Margin Guarantee',
  PRODUCER_PREMIUM: 'Producer Premium',
} as const;

export type LgmCampaignPdfColumn =
  (typeof LgmCampaignPdfColumns)[keyof typeof LgmCampaignPdfColumns];

export const LrpCampaignPdfColumns = {
  ENDORSEMENT_LENGTH: 'Endorsement Length',
  END_DATE: 'End Date',
  EXPECTED_VALUE_PER_CWT: 'Expected Value Per Cwt',
  COVERAGE_PRICE_PER_CWT: 'Coverage Price Per Cwt',
  COVERAGE_LEVEL: 'Coverage Level',
  RATE: 'Rate',
  INSURED_VALUE: 'Insured Value',
  TOTAL_PREMIUM: 'Total Premium',
  SUBSIDY: 'Subsidy',
  PRODUCER_PREMIUM: 'Producer Premium',
  PRODUCER_PREMIUM_PER_CWT: 'Producer Premium Per Cwt',
  PRODUCER_PREMIUM_PER_HEAD: 'Producer Premium Per Head',
  CME_PUT_STRIKE: 'CME Put Strike',
  CME_PUT_PREMIUM_PER_CWT: 'CME Put Premium Per Cwt',
  FUTURES_CONTRACT_MONTH: 'Futures Contract Month',
} as const;

export type LrpCampaignPdfColumn =
  (typeof LrpCampaignPdfColumns)[keyof typeof LrpCampaignPdfColumns];

export const lrpCampaignPdfTitleMap: Record<LrpCampaignPdfColumn, string> = {
  [LrpCampaignPdfColumns.ENDORSEMENT_LENGTH]: 'Endorsement<br />Length (weeks)',
  [LrpCampaignPdfColumns.END_DATE]: 'End Date',
  [LrpCampaignPdfColumns.COVERAGE_LEVEL]: 'Coverage<br />Level',
  [LrpCampaignPdfColumns.EXPECTED_VALUE_PER_CWT]: 'Expected<br />Value (/cwt)',
  [LrpCampaignPdfColumns.COVERAGE_PRICE_PER_CWT]: 'Coverage<br />Price (/cwt)',
  [LrpCampaignPdfColumns.RATE]: 'Rate',
  [LrpCampaignPdfColumns.INSURED_VALUE]: 'Insured<br />Value',
  [LrpCampaignPdfColumns.TOTAL_PREMIUM]: 'Total<br />Premium',
  [LrpCampaignPdfColumns.SUBSIDY]: 'Subsidy',
  [LrpCampaignPdfColumns.PRODUCER_PREMIUM]: 'Producer<br />Premium',
  [LrpCampaignPdfColumns.PRODUCER_PREMIUM_PER_CWT]:
    'Producer<br />Premium (/cwt)',
  [LrpCampaignPdfColumns.PRODUCER_PREMIUM_PER_HEAD]:
    'Producer<br />Premium (/head)',
  [LrpCampaignPdfColumns.CME_PUT_STRIKE]: 'CME Put<br />Strike',
  [LrpCampaignPdfColumns.CME_PUT_PREMIUM_PER_CWT]:
    'CME Put<br />Premium (/cwt)',
  [LrpCampaignPdfColumns.FUTURES_CONTRACT_MONTH]: 'Futures<br />Contract Month',
};

export const lgmCampaignPdfTitleMap: Record<LgmCampaignPdfColumn, string> = {
  [LgmCampaignPdfColumns.MARKETING_MONTH]: 'Marketing Month',
  [LgmCampaignPdfColumns.DEDUCTIBLE]: 'Deductible',
  [LgmCampaignPdfColumns.GROSS_MARGIN_GUARANTEE]: 'Gross Margin<br />Guarantee',
  [LgmCampaignPdfColumns.PRODUCER_PREMIUM]: 'Producer<br />Premium',
};

export const InsureIQQuoteTableHeaderTexts = {
  LRP: {
    title: 'Livestock Risk Protection (LRP) Quote',
    columnHeaders: [
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.ENDORSEMENT_LENGTH],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.END_DATE],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.EXPECTED_VALUE_PER_CWT],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.COVERAGE_PRICE_PER_CWT],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.COVERAGE_LEVEL],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.RATE],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.INSURED_VALUE],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.TOTAL_PREMIUM],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.SUBSIDY],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.PRODUCER_PREMIUM],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.PRODUCER_PREMIUM_PER_CWT],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.PRODUCER_PREMIUM_PER_HEAD],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.CME_PUT_STRIKE],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.CME_PUT_PREMIUM_PER_CWT],
      lrpCampaignPdfTitleMap[LrpCampaignPdfColumns.FUTURES_CONTRACT_MONTH],
    ],
    uom: '',
  },
  LGM: {
    title: 'Livestock Gross Margin (LGM) Quote',
    columnHeaders: [
      lgmCampaignPdfTitleMap[LgmCampaignPdfColumns.MARKETING_MONTH],
      lgmCampaignPdfTitleMap[LgmCampaignPdfColumns.DEDUCTIBLE],
      lgmCampaignPdfTitleMap[LgmCampaignPdfColumns.GROSS_MARGIN_GUARANTEE],
      lgmCampaignPdfTitleMap[LgmCampaignPdfColumns.PRODUCER_PREMIUM],
    ],
    uom: '($/head)',
  },
};

export const COMBINED_CAMPAIGN_EMAIL_DEFAULT_MESSAGE =
  'Please see attached for the latest quotes.';
